import { dramaWorks } from "@/mocks/works/drama";
import { movieWorks } from "@/mocks/works/movie";
import { webWorks } from "@/mocks/works/web";
import type { Work, WorkType } from "@/types/work";

export const allWorks: Work[] = [
  ...dramaWorks,
  ...movieWorks,
  ...webWorks,
] as Work[];

export function getWorkById(id: string): Work | undefined {
  return allWorks.find((work) => work.id === id);
}

export function getWorksByType(type: string): Work[] {
  return allWorks.filter((work) => work.type === type);
}

export function getFeaturedWorks(limit = 5): Work[] {
  const featured = allWorks.filter((work) => work.featured).slice(0, limit);
  if (featured.length >= limit) return featured;
  const rest = allWorks.filter((work) => !work.featured);
  return [...featured, ...rest].slice(0, limit);
}

export function getHotWorks(limit = 6): Work[] {
  return [...allWorks].sort((a, b) => b.heat - a.heat).slice(0, limit);
}

export function getLatestWorks(limit = 8): Work[] {
  return [...allWorks]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, limit);
}

export function getRelatedWorks(work: Work, limit = 4): Work[] {
  const sameType = allWorks.filter(
    (item) => item.id !== work.id && item.type === work.type,
  );
  const sameGenre = sameType.filter((item) =>
    item.genres.some((genre) => work.genres.includes(genre)),
  );
  const others = sameType.filter((item) => !sameGenre.includes(item));
  const merged = [...sameGenre, ...others];
  if (merged.length >= limit) return merged.slice(0, limit);
  const fill = allWorks.filter(
    (item) => item.id !== work.id && !merged.includes(item),
  );
  return [...merged, ...fill].slice(0, limit);
}

export function searchWorks(keyword: string): Work[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return allWorks.filter((work) => {
    const haystack = [
      work.title,
      work.shortIntro,
      work.synopsis,
      work.director,
      work.region,
      ...work.genres,
      ...work.cast,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(query);
  });
}

export function getTypeCount(type: WorkType): number {
  return allWorks.filter((work) => work.type === type).length;
}